import { type AnyElement } from "@zyzgroup/core-common";
import {
  atomReadAttributes,
  atomWriteAttribute,
  atomRemoveStyle,
  atomWriteStyle
} from "./hooks";
import { createSVGElement, createSvgPath } from "./hooks/useDraw/svg";

// Virtual DOM 其实就是一棵以 VNode节点 作为基础的树
export interface VNode {
  name: string;
  tag: string;
  props: { [index: string]: any };
  children: string | VNode[];
  // 页面初始存在el 或 挂载后才会有el
  el?: AnyElement;
  // 下次更新时的来源
  alternate?: VNode;
}

let vnodeId = 0;

/// 将 tag 转化为 vnode
export function h(
  tag: string,
  props: { [index: string]: any } = {},
  children: string | VNode[] = [],
  el?: AnyElement
): VNode {
  return {
    name: String(++vnodeId),
    tag,
    props,
    children,
    el
  };
}

// 前提：el已挂载到dom上！
export function hFromEl(el: AnyElement): VNode {
  return h(
    el.tagName,
    atomReadAttributes(el),
    Array.from(el.children).map((e) => hFromEl(e as AnyElement)),
    el
  );
}

/// 将 vnode 挂载到 真实dom 上
export function mount(
  vnode: VNode,
  parentContainer: AnyElement = document.documentElement
): AnyElement {
  if (vnode.el) {
    return vnode.el;
  }
  vnode.el =
    vnode.tag == "path" ? createSvgPath() : document.createElement(vnode.tag);
  if (vnode.props) {
    for (const k in vnode.props) {
      const v = vnode.props[k];
      if (k.startsWith("on")) {
        vnode.el.addEventListener(k.slice(2).toLowerCase(), v);
      } else if (k === "style") {
        atomWriteStyle(vnode.el, vnode.props[k]);
      } else {
        atomWriteAttribute(vnode.el, k, v);
      }
    }
  }
  if (vnode.children) {
    if (typeof vnode.children == "string") {
      vnode.el.appendChild(document.createTextNode(vnode.children));
      // vnode.el.textContent = vnode.children;
      // vnode.el.innerHTML = vnode.children;
    } else {
      vnode.children.forEach((item) => {
        mount(item, vnode.el);
      });
    }
  }
  parentContainer.appendChild(vnode.el);
  return vnode.el;
}

/// diff vnode to 真实dom
export function patch(oldVnode: VNode, newVnode: VNode): void {
  if (!oldVnode.el) {
    // if (oldVnode.tag == newVnode.tag) {
    //   mergeObjectDeep(newVnode.props, oldVnode.props, false);
    // }
    // mount(newVnode);
    return;
  }
  // 不同 tag
  if (oldVnode.tag !== newVnode.tag) {
    (oldVnode.el.parentElement || document.body).replaceChild(
      mount(newVnode),
      oldVnode.el
    );
    return;
  }
  // 相同 tag
  const el = (newVnode.el = oldVnode.el);
  const oldProps = oldVnode.props;
  const newProps = newVnode.props;
  for (const k in oldProps) {
    if (!(k in newProps)) {
      if (k.startsWith("on")) {
        el.removeEventListener(k.slice(2).toLowerCase(), oldProps[k]);
      } else {
        el.removeAttribute(k);
      }
    }
  }
  for (const k in newProps) {
    const oldValue = oldProps[k];
    const newValue = newProps[k];
    if (newValue != oldValue) {
      if (k.startsWith("on")) {
        const name = k.slice(2).toLowerCase();
        el.removeEventListener(name, oldValue);
        el.addEventListener(name, newValue);
      } else if (k === "style") {
        for (const sk in oldValue) {
          if (!(sk in newValue)) {
            atomRemoveStyle(el, sk);
          }
        }
        atomWriteStyle(el, newValue);
      } else {
        atomWriteAttribute(el, k, newValue);
      }
    }
  }
  const oldChildren = oldVnode.children || [];
  const newChildren = newVnode.children || [];
  if (typeof newChildren == "string") {
    // el.textContent = newChildren;
    el.innerHTML = newChildren;
  } else {
    if (typeof oldChildren == "string") {
      el.innerHTML = "";
      newChildren.forEach((item) => {
        mount(item, el);
      });
    } else {
      const commonLength = Math.min(oldChildren.length, newChildren.length);
      for (let i = 0; i < commonLength; i++) {
        patch(oldChildren[i], newChildren[i]);
      }
      if (newChildren.length > oldChildren.length) {
        newChildren.slice(oldChildren.length).forEach((item) => {
          mount(item, el);
        });
      }
      if (newChildren.length < oldChildren.length) {
        oldChildren.slice(newChildren.length).forEach((item) => {
          if (item.el) {
            el.removeChild(item.el);
          }
        });
      }
    }
  }
}
